import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CheckCircle2, BadgeCheck, FileCheck, ShieldCheck, MessageCircle, ArrowRight } from "lucide-react";
import { BRAND } from "@/lib/brand";

const features = [
  { Icon: CheckCircle2, t: "Eligibility Review", d: "We assess your brand against Meta's Green Tick eligibility criteria before you apply." },
  { Icon: BadgeCheck, t: "Business Profile Optimization", d: "Display name, about, category and verifiable presence — tuned for the best approval odds." },
  { Icon: FileCheck, t: "Meta Application Assistance", d: "We prepare your Official Business Account application and submit it through the right channel." },
  { Icon: ShieldCheck, t: "Approval Guidance", d: "If Meta asks for changes, we coach you through clarifications and re-submissions." },
  { Icon: MessageCircle, t: "Dedicated Support Team", d: `Talk to a verification expert between ${BRAND.support.hours} IST — phone, WhatsApp or email.` },
];

export const GreenTick = ({ onTalkToExpert }) => {
  return (
    <section id="green-tick" className="py-20 sm:py-28 bg-white overflow-hidden" data-testid="greentick-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-emerald-600">Green Tick Verification</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Get WhatsApp Green Tick Verification Support
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Our team helps eligible businesses prepare and submit their Official Business Account (Green Tick) application with Meta.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-800">
              <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" />
              Final approval at Meta&apos;s discretion · We maximise your chances.
            </div>

            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <Button
                onClick={onTalkToExpert}
                data-testid="greentick-talk-to-expert"
                className="w-full sm:w-auto rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-6 h-12 text-base font-semibold shadow-[0_10px_30px_-12px_rgba(16,185,129,0.6)]"
              >
                Talk to an Expert <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <a
                href={BRAND.whatsapp.waLink}
                target="_blank"
                rel="noreferrer"
                data-testid="greentick-whatsapp"
                className="w-full sm:w-auto inline-flex items-center justify-center rounded-full h-12 px-6 text-base font-semibold bg-white text-emerald-700 border border-emerald-200 hover:bg-emerald-50 transition-all"
              >
                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Now
              </a>
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {features.map(({ Icon, t, d }, i) => (
              <motion.div
                key={t}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                data-testid={`greentick-card-${i}`}
                className="rounded-3xl border border-slate-200/70 bg-white p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 ring-4 ring-emerald-100">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">{t}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
